import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

interface HorizontalScrollArrowsProps {
  children: React.ReactNode;
  scrollContainerProps?: object;
  style?: object;
}

const SCROLL_BUTTON_WIDTH = 56;

export const ProjectHorizontalScrollButtons: React.FC<
  HorizontalScrollArrowsProps
> = ({ children, scrollContainerProps, style }) => {
  const theme = useTheme();
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const paperColor = theme.palette.background.paper;
  const fadeColor = alpha(paperColor, 0.9);
  const fadeTransparent = alpha(paperColor, 0);

  useEffect(() => {
    const updateArrows = () => {
      const el = scrollContainerRef.current;
      if (!el) return;
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setShowLeftArrow(scrollLeft > 8);
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 8);
    };
    updateArrows();
    const el = scrollContainerRef.current;
    if (el) {
      el.addEventListener("scroll", updateArrows);
      window.addEventListener("resize", updateArrows);
    }
    return () => {
      if (el) el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [children]);

  const handleScroll = (direction: "left" | "right") => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.8;
    if (direction === "left") {
      el.scrollBy({ left: -scrollAmount, behavior: "smooth" });
    } else {
      el.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <Box sx={{ position: "relative", width: "100%", ...style }}>
      {showLeftArrow && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            width: SCROLL_BUTTON_WIDTH,
            display: "flex",
            alignItems: "center",
            zIndex: 2,
            pointerEvents: "none",
            background: `linear-gradient(to right, ${fadeColor}, ${fadeTransparent})`,
          }}
        >
          <Box sx={{ pointerEvents: "auto", marginLeft: 0.5 }}>
            <IconButton
              sx={{
                backgroundColor: paperColor,
                boxShadow: 2,
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
              size="large"
              onClick={() => handleScroll("left")}
              aria-label="Scroll left"
            >
              <KeyboardArrowLeft />
            </IconButton>
          </Box>
        </Box>
      )}
      {showRightArrow && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            height: "100%",
            width: SCROLL_BUTTON_WIDTH,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            zIndex: 2,
            pointerEvents: "none",
            background: `linear-gradient(to left, ${fadeColor}, ${fadeTransparent})`,
          }}
        >
          <Box sx={{ pointerEvents: "auto", marginRight: 0.5 }}>
            <IconButton
              sx={{
                backgroundColor: paperColor,
                boxShadow: 2,
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
              size="large"
              onClick={() => handleScroll("right")}
              aria-label="Scroll right"
            >
              <KeyboardArrowRight />
            </IconButton>
          </Box>
        </Box>
      )}
      <Box
        ref={scrollContainerRef}
        sx={{ overflowX: "auto", width: "100%" }}
        {...scrollContainerProps}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ProjectHorizontalScrollButtons;
